import Badge from "@/components/common/Badge";
import Card from "@/components/common/Card";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import PageHeader from "@/components/common/PageHeader";
import LeaderboardCard from "@/components/leaderboard/LeaderboardCard";
import { useAuth } from "@/hooks/useAuth";
import { useLeaderboardData, DepartmentRankingEntry } from "@/hooks/queries/useLeaderboardQueries";
import { Tab } from "@headlessui/react";
import {
  ChartBarIcon,
  StarIcon,
  TrophyIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

interface LeaderboardEntry {
  _id: string;
  name: string;
  email: string;
  department: string;
  quizScore: number;
  score: number;
  completedTutorials: number;
  change: number;
}


const LeaderboardPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: leaderboardData, isLoading, error } = useLeaderboardData();

  // Extract data from the hook response
  const leaderboard = leaderboardData?.individualUsers || [];
  const departmentRankings = leaderboardData?.departmentRankings || [];

  // Handle error state
  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {t("leaderboard.errorLoading")}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {t("leaderboard.errorMessage")}
          </p>
        </div>
      </div>
    );
  }

  const getTopPerformers = () => {
    // Get top 3 users across ALL departments based on points (global ranking)
    return leaderboard.slice(0, 3);
  };

  const getDepartmentBasedRanking = () => {
    const departmentGroups: { [key: string]: LeaderboardEntry[] } = {};

    // Group entries by department
    leaderboard.forEach((entry) => {
      if (!departmentGroups[entry.department]) {
        departmentGroups[entry.department] = [];
      }
      departmentGroups[entry.department].push(entry);
    });

    // Sort each department's entries by score (highest first)
    Object.keys(departmentGroups).forEach((dept) => {
      departmentGroups[dept].sort((a, b) => b.quizScore - a.quizScore);
    });

    // Create ranked entries with department-based ranking
    const rankedEntries: (LeaderboardEntry & { departmentRank: number })[] = [];
    Object.keys(departmentGroups).forEach((dept) => {
      departmentGroups[dept].forEach((entry, index) => {
        rankedEntries.push({
          ...entry,
          departmentRank: index + 1,
        });
      });
    });

    // Sort by department first, then by department rank
    return rankedEntries.sort((a, b) => {
      if (a.department !== b.department) {
        return a.department.localeCompare(b.department);
      }
      return a.departmentRank - b.departmentRank;
    });
  };

  const getCurrentUserRank = () => {
    // Find user's rank in the global leaderboard (for top performers)
    return leaderboard.findIndex((entry) => entry.name === user?.name) + 1;
  };

  const getCurrentUserDepartmentRank = () => {
    // Find user's rank within their department
    const userEntry = leaderboard.find((entry) => entry.name === user?.name);
    if (!userEntry) return 0;

    const departmentUsers = leaderboard.filter(
      (entry) => entry.department === userEntry.department
    );
    const sortedDepartmentUsers = departmentUsers.sort(
      (a, b) => b.quizScore - a.quizScore
    );
    return (
      sortedDepartmentUsers.findIndex(
        (entry) => entry.name === userEntry.name
      ) + 1
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-primary-50 to-primary-100/50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="flex items-center justify-center min-h-screen">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-primary-50 to-primary-100/50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8">
        {/* Header Section */}
        <PageHeader
          title={t("leaderboard.leaderboard")}
          description={t("leaderboard.trackPerformanceAndCelebrate")}
        />

        {/* Top Performers Section */}
        {leaderboard.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {t("leaderboard.topPerformers")}
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                {t("leaderboard.celebratingExcellence")}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
              {getTopPerformers().map((entry, index) => (
                <motion.div
                  key={entry.name}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`relative p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-lg ${
                    index === 0
                      ? "bg-gradient-to-br from-yellow-400 to-yellow-600 text-white"
                      : index === 1
                      ? "bg-gradient-to-br from-gray-300 to-gray-500 text-white"
                      : "bg-gradient-to-br from-amber-600 to-amber-800 text-white"
                  }`}
                >
                  <div className="absolute -top-3 -right-3">
                    {index === 0 && (
                      <TrophyIcon className="w-8 h-8 text-yellow-200" />
                    )}
                    {index === 1 && (
                      <StarIcon className="w-8 h-8 text-gray-200" />
                    )}
                    {index === 2 && (
                      <StarIcon className="w-8 h-8 text-amber-200" />
                    )}
                  </div>

                  <div className="text-center">
                    <div className="text-2xl sm:text-4xl font-bold mb-2">#{index + 1}</div>
                    <div className="text-base sm:text-xl font-semibold mb-1 truncate">
                      {entry.name}
                    </div>
                    <div className="text-xs sm:text-sm opacity-90 mb-2 sm:mb-3 truncate">
                      {entry.department}
                    </div>
                    <div className="text-2xl sm:text-3xl font-bold">{entry.quizScore}</div>
                    <div className="text-xs sm:text-sm opacity-90">{t("leaderboard.points")}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Current User Position */}
        {user && getCurrentUserRank() > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mb-8"
          >
            <Card className="bg-gradient-to-r from-primary-500 to-primary-600 text-white">
              <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-4">
                <div className="flex items-center space-x-3 sm:space-x-4 min-w-0 flex-1">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-lg sm:text-xl font-bold">
                      {getCurrentUserRank()}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold truncate">
                      {t("leaderboard.yourGlobalPosition")}
                    </h3>
                    <p className="text-xs sm:text-sm text-primary-100 truncate">{t("leaderboard.keepUpGreatWork")}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                  <Badge
                    variant="success"
                    className="bg-white/20 text-white border-white/30 text-xs whitespace-nowrap"
                  >
                    #{getCurrentUserRank()} {t("leaderboard.ofGlobally", { count: leaderboard.length })}
                  </Badge>
                  {getCurrentUserDepartmentRank() > 0 && (
                    <Badge
                      variant="warning"
                      className="bg-white/20 text-white border-white/30 text-xs whitespace-nowrap"
                    >
                      #{getCurrentUserDepartmentRank()} {t("leaderboard.inDepartment", { 
                        department: leaderboard.find((entry) => entry.name === user?.name)?.department 
                      })}
                    </Badge>
                  )}
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Main Leaderboard Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Tab.Group>
            <Tab.List className="flex space-x-1 rounded-xl bg-white/80 dark:bg-gray-800/80 p-1 backdrop-blur-sm shadow-lg mb-6 sm:mb-8">
              <Tab
                className={({ selected }) =>
                  `w-full rounded-lg py-3 px-4 text-sm font-medium leading-5 transition-all duration-200 ${
                    selected
                      ? "bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg"
                      : "text-gray-700 dark:text-gray-300 hover:bg-white/50 dark:hover:bg-gray-700/50"
                  }`
                }
              >
                <div className="flex items-center justify-center space-x-1 sm:space-x-2">
                  <UsersIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline">{t("leaderboard.individualRankings")}</span>
                  <span className="sm:hidden">Individual</span>
                </div>
              </Tab>
              <Tab
                className={({ selected }) =>
                  `w-full rounded-lg py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm font-medium leading-5 transition-all duration-200 ${
                    selected
                      ? "bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-lg"
                      : "text-gray-700 dark:text-gray-300 hover:bg-white/50 dark:hover:bg-gray-700/50"
                  }`
                }
              >
                <div className="flex items-center justify-center space-x-1 sm:space-x-2">
                  <ChartBarIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline">{t("leaderboard.departmentRankings")}</span>
                  <span className="sm:hidden">Departments</span>
                </div>
              </Tab>
            </Tab.List>

            <Tab.Panels>
              {/* Individual Leaderboard Panel */}
              <Tab.Panel>
                <Card className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-xl">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                        {t("leaderboard.individualPerformance")}
                      </h3>
                      <Badge
                        variant="primary"
                        className="bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300"
                      >
                        {leaderboard.length} {t("leaderboard.participants")}
                      </Badge>
                    </div>

                    {leaderboard.length > 0 ? (
                      <div className="space-y-3">
                        {getDepartmentBasedRanking().map((entry, index) => (
                          <LeaderboardCard
                            key={entry.name + index}
                            rank={entry.departmentRank}
                            name={entry.name}
                            department={entry.department}
                            score={entry.quizScore}
                            change={entry.change}
                            isCurrentUser={user?.name === entry.name}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <TrophyIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                          {t("leaderboard.noDataAvailable")}
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400">
                          {t("leaderboard.leaderboardDataWillAppear")}
                        </p>
                      </div>
                    )}
                  </div>
                </Card>
              </Tab.Panel>

              {/* Department Leaderboard Panel */}
              <Tab.Panel>
                <Card className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-xl">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                        {t("leaderboard.departmentPerformance")}
                      </h3>
                      <Badge
                        variant="primary"
                        className="bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300"
                      >
                        {departmentRankings.length} {t("leaderboard.departments")}
                      </Badge>
                    </div>

                    {departmentRankings.length > 0 ? (
                      <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
                        <div className="overflow-x-auto -mx-2 sm:mx-0">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800">
                              <tr>
                                <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-[10px] sm:text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                                  {t("leaderboard.department")}
                                </th>
                                <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-[10px] sm:text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                                  {t("leaderboard.members")}
                                </th>
                                <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-[10px] sm:text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                                  {t("leaderboard.avgScore")}
                                </th>
                                <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-[10px] sm:text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                                  {t("leaderboard.totalScore")}
                                </th>
                                <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-[10px] sm:text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                                  {t("leaderboard.performance")}
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                              {departmentRankings.map((dept, idx) => {
                                const performance =
                                  dept.averageScore >= 70
                                    ? "excellent"
                                    : dept.averageScore >= 50
                                    ? "good"
                                    : dept.averageScore >= 30
                                    ? "fair"
                                    : "needs-improvement";

                                return (
                                  <motion.tr
                                    key={idx}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{
                                      duration: 0.3,
                                      delay: idx * 0.05,
                                    }}
                                    className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200"
                                  >
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="flex items-center">
                                        <div className="w-8 h-8 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg flex items-center justify-center text-white text-sm font-bold mr-3">
                                          {idx + 1}
                                        </div>
                                        <div>
                                          <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                            {dept.department}
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="flex items-center">
                                        <UsersIcon className="w-4 h-4 text-gray-400 mr-2" />
                                        <span className="text-sm text-gray-900 dark:text-white font-medium">
                                          {dept.members}
                                        </span>
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="flex items-center">
                                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                          {dept.averageScore}%
                                        </span>
                                        <div className="ml-2 w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                          <div
                                            className={`h-2 rounded-full ${
                                              performance === "excellent"
                                                ? "bg-green-500"
                                                : performance === "good"
                                                ? "bg-primary-500"
                                                : performance === "fair"
                                                ? "bg-yellow-500"
                                                : "bg-red-500"
                                            }`}
                                            style={{
                                              width: `${Math.min(dept.averageScore, 100)}%`,
                                            }}
                                          />
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                        {dept.totalScore}
                                      </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <Badge
                                        variant={
                                          performance === "excellent"
                                            ? "success"
                                            : performance === "good"
                                            ? "primary"
                                            : performance === "fair"
                                            ? "warning"
                                            : "error"
                                        }
                                        className="capitalize"
                                      >
                                        {t(`leaderboard.${performance.replace("-", "")}`)}
                                      </Badge>
                                    </td>
                                  </motion.tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <ChartBarIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                          {t("leaderboard.noDepartmentData")}
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400">
                          {t("leaderboard.departmentRankingsWillAppear")}
                        </p>
                      </div>
                    )}
                  </div>
                </Card>
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        </motion.div>
      </div>
    </div>
  );
};

export default LeaderboardPage;
